import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, FileText, MessageSquare, LogOut, Settings, Upload, Save, Volume2, Play, Pause, Send, UserPlus, Lock } from 'lucide-react';
import { getIntro, updateIntro, API_BASE_URL } from '../api';
import AudioRecorder from '../components/AudioRecorder';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [introText, setIntroText] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role === 'agent') { navigate('/agent'); return; }
    loadIntro();
  }, [navigate]);

  const loadIntro = async () => {
    try {
      const res = await getIntro();
      setIntroText(res.data.text || '');
      setCurrentAudioUrl(res.data.audio_url);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/admin'); }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('text', introText);
      if (audioFile) {
        formData.append('audio', audioFile);
      }
      if (!currentAudioUrl) {
        formData.append('remove_audio', 'true');
      }
      await updateIntro(formData);
      toast.success('Introduction mise à jour');
      loadIntro();
      setAudioFile(null);
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const handleLogout = () => { localStorage.clear(); navigate('/admin'); };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7f5' }}>
      {/* Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '240px', background: 'linear-gradient(180deg, #1B5E20, #2E7D32)', padding: '24px 16px', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', padding: '0 8px' }}>
          <img src="/logo.webp" alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', padding: '2px' }} />
          <div>
            <div style={{ color: '#FFD54F', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px' }}>ADMIN</div>
            <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Khombole</div>
          </div>
        </div>
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[
            { icon: <BarChart3 size={18} />, label: 'Tableau de bord', path: '/admin/dashboard' },
            { icon: <FileText size={18} />, label: 'Questions', path: '/admin/questions' },
            { icon: <MessageSquare size={18} />, label: 'Réponses', path: '/admin/responses' },
            { icon: <UserPlus size={18} />, label: 'Agents', path: '/admin/agents' },
            { icon: <Settings size={18} />, label: 'Paramètres', path: '/admin/settings', active: true },
            { icon: <Lock size={18} />, label: 'Mon Compte', path: '/admin/profile' },
          ].map((item, i) => (
            <button key={i} onClick={() => navigate(item.path)}
              style={{ background: item.active ? 'rgba(255,255,255,0.15)' : 'transparent', border: 'none', color: 'white', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: item.active ? '600' : '400', textAlign: 'left', width: '100%' }}>
              {item.icon} {item.label}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <LogOut size={18} /> Déconnexion
        </button>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '240px', padding: '32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', fontFamily: 'Playfair Display, serif' }}>Paramètres</h1>
          <p style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>Gérez l'introduction de l'enquête</p>
        </div>

        {/* Intro Audio Section */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 2px 16px rgba(0,0,0,0.04)', maxWidth: '700px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#FFF8E1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Volume2 size={20} color="#FFA000" />
            </div>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>Audio d'introduction</h3>
              <p style={{ fontSize: '12px', color: '#888' }}>Audio joué au début de l'enquête pour expliquer le contexte</p>
            </div>
          </div>

          {/* Current audio */}
          {currentAudioUrl && (
            <div style={{ background: '#f8faf8', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e8e8e8' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>Audio actuel :</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <audio ref={audioRef} src={currentAudioUrl ? `${API_BASE_URL}${currentAudioUrl}` : ''} onEnded={() => setPlaying(false)} />
                <button onClick={togglePlay}
                  style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 10px rgba(46,125,50,0.2)' }}>
                  {playing ? <Pause size={16} color="white" /> : <Play size={16} color="white" style={{ marginLeft: '1px' }} />}
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>Audio d'introduction</div>
                  <div style={{ fontSize: '11px', color: '#999' }}>Cliquez pour écouter</div>
                </div>
              </div>
            </div>
          )}

          {/* Upload new audio */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
              {currentAudioUrl ? 'Remplacer l\'audio :' : 'Uploader un audio :'}
            </label>
            <AudioRecorder
              existingUrl={currentAudioUrl ? `${API_BASE_URL}${currentAudioUrl}` : null}
              onRecord={(file) => setAudioFile(file)}
              onClear={() => setAudioFile(null)}
              onDeleteExisting={() => setCurrentAudioUrl(null)}
            />
            {audioFile && !audioFile.name?.startsWith('recorded_') && (
              <div style={{ border: '2px dashed #e0e0e0', borderRadius: '12px', padding: '16px', textAlign: 'center', marginTop: '8px' }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#4CAF50'; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; }}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#e0e0e0'; if (e.dataTransfer.files[0]) setAudioFile(e.dataTransfer.files[0]); }}>
                <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files[0])} style={{ display: 'none' }} id="intro-audio-upload" />
                <label htmlFor="intro-audio-upload" style={{ cursor: 'pointer' }}>
                  <Upload size={20} color="#999" style={{ marginBottom: '6px' }} />
                  <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
                    {audioFile ? audioFile.name : 'Cliquez ou glissez un fichier audio ici'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>MP3, WAV, OGG, WEBM (max 20 MB)</div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Intro Text Section */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 2px 16px rgba(0,0,0,0.04)', maxWidth: '700px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={20} color="#2E7D32" />
            </div>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>Texte d'introduction</h3>
              <p style={{ fontSize: '12px', color: '#888' }}>Texte affiché sur l'écran d'accueil de l'enquête</p>
            </div>
          </div>

          <textarea value={introText} onChange={(e) => setIntroText(e.target.value)} rows={6}
            style={{ width: '100%', padding: '14px', border: '1.5px solid #e0e0e0', borderRadius: '10px', fontSize: '14px', outline: 'none', resize: 'vertical', fontFamily: 'Inter, sans-serif', lineHeight: '1.7', transition: 'border-color 0.2s' }}
            onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            placeholder="Texte d'introduction de l'enquête..." />
        </div>

        {/* Save Button */}
        <button onClick={handleSave} disabled={saving}
          style={{ background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', color: 'white', border: 'none', padding: '14px 32px', borderRadius: '12px', cursor: saving ? 'wait' : 'pointer', fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 16px rgba(46,125,50,0.25)', transition: 'all 0.3s' }}
          onMouseOver={(e) => { if (!saving) e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
          <Save size={16} />
          {saving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;
